// ─── Command Parser ───────────────────────────────────────────────────────────
// Parses raw user input into a matched command from the registry.
// Entirely deterministic — no AI, no fuzzy search libraries.

import { getAllAliases } from './commandRegistry';

/**
 * Normalize input: trim whitespace, lowercase, collapse multiple spaces.
 */
export function normalizeInput(raw) {
  return raw.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Parse a normalized input string against the registry.
 * Returns the matched command object, or null if no match.
 */
export function parseCommand(rawInput, registry) {
  const input = normalizeInput(rawInput);
  if (!input) return null;

  const allAliases = getAllAliases(registry);

  // 1. Exact match
  const exactMatch = allAliases.find((a) => a.alias === input);
  if (exactMatch) return exactMatch.command;

  // 2. Prefix match — e.g. "open thread" matches "open thread-magic"
  const prefixMatch = allAliases.find((a) => a.alias.startsWith(input));
  if (prefixMatch) return prefixMatch.command;

  return null;
}

/**
 * Get autocomplete suggestions for a partial input.
 * Returns array of alias strings (max 6).
 */
export function getAutocomplete(partialRaw, registry) {
  const partial = normalizeInput(partialRaw);
  if (!partial) return [];

  const allAliases = getAllAliases(registry);

  // Deduplicate and filter
  const seen = new Set();
  const results = [];

  for (const { alias } of allAliases) {
    if (alias.startsWith(partial) && alias !== partial) {
      if (!seen.has(alias)) {
        seen.add(alias);
        results.push(alias);
      }
    }
    if (results.length >= 6) break;
  }

  return results;
}
